import { NavLink, Outlet } from 'react-router-dom';
import { SIDEBAR_NAV, type SidebarItem } from '@/config/sidebar';
import {
  canAccessModule,
  type Permission,
  USER_ROLE_LABELS,
} from '@/config/permissions';
import { routePaths } from '@/routes/paths';
import { NotificationDropdown } from '@/modules/notifications';
import { useAuth } from '@/shared/hooks/useAuth';
import type { UserRole } from '@/types/user';

function isItemVisible(
  item: SidebarItem,
  role: UserRole | undefined,
  checkPerm: (permission: Permission) => boolean,
): boolean {
  if (item.permission && !checkPerm(item.permission)) return false;
  if (item.moduleId && !canAccessModule(role, item.moduleId)) return false;
  if (item.children?.length) {
    return item.children.some((child) => isItemVisible(child, role, checkPerm));
  }
  return true;
}

function SidebarLink({ item }: { item: SidebarItem }) {
  const Icon = item.icon;
  const linkProps = item.end === true ? { end: true as const } : {};
  return (
    <li>
      <NavLink
        to={item.path}
        {...linkProps}
        className={({ isActive }) =>
          isActive ? 'app-nav-link is-active' : 'app-nav-link'
        }
      >
        <Icon size={16} />
        <span>{item.label}</span>
      </NavLink>
      {item.children?.length ? (
        <ul className="app-nav app-nav--nested">
          {item.children.map((child) => (
            <SidebarLink key={child.id} item={child} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export function AppLayout() {
  const { profile, signOut, hasPermission: checkPerm } = useAuth();
  const role = profile?.role;

  const visibleNav = SIDEBAR_NAV.filter((item) =>
    isItemVisible(item, role, checkPerm),
  );

  return (
    <div className="app-layout">
      <header className="app-header">
        <NavLink to={routePaths.home} className="app-brand">
          ERP ASAP
        </NavLink>
        <div className="app-header-user">
          {profile?.active && profile.role !== 'pending' ? (
            <NotificationDropdown />
          ) : null}
          {profile ? (
            <>
              {profile.photoURL ? (
                <img
                  src={profile.photoURL}
                  alt=""
                  className="app-header-avatar"
                  width={28}
                  height={28}
                />
              ) : null}
              <span className="app-user-name">{profile.displayName}</span>
              <span className="app-user-role">
                {USER_ROLE_LABELS[profile.role]}
              </span>
            </>
          ) : null}
          <button
            type="button"
            className="app-logout"
            onClick={() => void signOut()}
          >
            Salir
          </button>
        </div>
      </header>
      <div className="app-body">
        <nav className="app-sidebar" aria-label="ERP">
          <ul className="app-nav">
            {visibleNav.map((item) => (
              <SidebarLink key={item.id} item={item} />
            ))}
          </ul>
        </nav>
        <main className="app-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
